import React, { useMemo } from 'react';
import AtlasV2DorisDoubleDropdownChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisDoubleDropdownChart';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import useTrafficMarketShareCalculator from 'src/components/AtlasV2Components/Hooks/useTrafficMarketShareCalculator';
import {
  useTrafficMetricsStore,
  useTrafficMetricsWithFilters
} from 'src/zustandStores/TrafficMetrics/useTrafficMetricsStore';
import { useBrandEntity, useCategoryEntity, useSubcategoryEntity } from 'src/utils/Hooks/reduxSelectors';
import { OTHER_TRAFFIC_FILED_IDS } from 'src/components/AtlasV2Components/AtlasV2TrafficPageComponents/AtlasV2TotalTraffic/AtlasV2TotalTrafficTopEntity';
import { GroupByField } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useMainTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import {
  DorisDropdownFieldNameEnum,
  DorisGroupByFieldNameEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

export const useDorisEntityDropdownOptions = (
  indexName: DorisDropdownFieldNameEnum = DorisDropdownFieldNameEnum.Traffic
) => {
  const { isCategoryEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBrandEntity } = useBrandEntity();

  const dropdownOptions = useMemo(() => {
    if (isCategoryEntity) {
      return [
        { id: DorisGroupByFieldNameEnum.Brand, label: 'Brand', indexName },
        { id: DorisGroupByFieldNameEnum.Subcategory, label: 'Subcategory', indexName },
        { id: DorisGroupByFieldNameEnum.Manufacturer, label: 'Manufacturer', indexName }
      ];
    }

    if (isSubcategoryEntity) {
      return [
        { id: DorisGroupByFieldNameEnum.Brand, label: 'Brand', indexName },
        { id: DorisGroupByFieldNameEnum.Manufacturer, label: 'Manufacturer', indexName }
      ];
    }

    if (isBrandEntity) {
      if (indexName === DorisDropdownFieldNameEnum.Traffic) {
        return [{ id: 'keyword', label: 'Keyword', indexName }];
      }
      return [
        { id: DorisGroupByFieldNameEnum.Category, label: 'Category', indexName },
        { id: DorisGroupByFieldNameEnum.Subcategory, label: 'Subcategory', indexName }
      ];
    }

    return [
      { id: DorisGroupByFieldNameEnum.Brand, label: 'Brand', indexName },
      { id: DorisGroupByFieldNameEnum.Category, label: 'Category', indexName },
      { id: DorisGroupByFieldNameEnum.Subcategory, label: 'Subcategory', indexName },
      { id: DorisGroupByFieldNameEnum.Manufacturer, label: 'Manufacturer', indexName }
    ];
  }, [isCategoryEntity, isSubcategoryEntity, isBrandEntity, indexName]);

  return dropdownOptions;
};
/**
 * Component that displays a double dropdown chart for total traffic data.
 * Uses a custom market share calculator that gets denominator values from the TrafficMetrics Zustand store.
 */
const AtlasV2TotalTrafficDoubleDropdownTopEntity = ({ groupByFields }: { groupByFields?: GroupByField[] }) => {
  // Get the data fetcher and loading state from the metrics fetcher hook
  const { isLoading: isDataFetching } = useTrafficMetricsWithFilters();
  const dropdownOptions = useDorisEntityDropdownOptions();
  const { isBrandEntity } = useBrandEntity();

  // Get loading state and data from the metrics store
  const isMetricsLoading = useTrafficMetricsStore((state) => state.isLoading);

  // Get the market share calculator and data loaded state for total clicks
  const { calculateMarketShare, isDataLoaded } = useTrafficMarketShareCalculator({
    trafficType: 'totalClicks'
  });
  const isTrafficDataLoading = isMetricsLoading || isDataFetching || !isDataLoaded;
  const entityPhrase = useEntityPhrasing();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimeDisplayName = useComparisonTimePeriodShortDisplayName();
  const taxonomyText = useTaxonomySubtitleText();

  return (
    <AtlasV2DorisDoubleDropdownChart
      groupByFields={groupByFields || dropdownOptions}
      fields={[
        {
          fieldName: 'totalClicks',
          label: 'Total Traffic',
          metricType: 'totalClicks',
          metricTypeDisplay: METRICTYPE.VOLUME
        },
        {
          fieldName: 'totalClicksShare',
          label: 'Share of Total Traffic',
          metricType: 'totalClicks',
          isMarketShare: true,
          metricTypeDisplay: METRICTYPE.PERCENT,
          ...(isBrandEntity ? {} : { marketShareCalculator: calculateMarketShare })
        }
      ]}
      queryKey="totalClicks_doubleDropdown"
      endPoint="GetTrafficByType"
      isLoading={isTrafficDataLoading}
      chartContextId={CHART_NAMES.TRAFFIC_TOP_ENTITY_COLUMN}
      preProcessData={(data) => {
        const otherTraffics = data.filter((datum) => OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        const remainingData = data.filter((datum) => !OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        return [...remainingData, ...otherTraffics];
      }}
      beaconChartWithLegendPropsBuilder={(props) => {
        return {
          ...props,
          includeSummaryInfo: false,
          loadingComponent: <SplineChartLoading includeSubtitle />,
          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1" lineHeight="24px">
                {`The chart displays the ${props.selectedField.label.toLocaleLowerCase()} for ${entityPhrase} in each
                ${props.selectedGroupByField.toLocaleLowerCase()} ${taxonomyText} during the ${mainTimePeriodShortDisplayName} period
                compared to the ${comparisonTimeDisplayName}.`}
                <SubtitleFilters showFilterNumber={false} />
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
    />
  );
};

export default AtlasV2TotalTrafficDoubleDropdownTopEntity;
